import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { ArrowRight, Sparkles, Target, BookOpen, Users, Trophy, Brain, Clock, CheckCircle2, Quote, Star, Send, Phone, CalendarDays, GraduationCap } from "lucide-react";

export default function Landing() {
  const [stats, setStats] = useState({ questions: 20000, students_helped: 500, avg_grade_improvement: 2, years_experience: 10 });
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    api.get("/stats/public").then(r => setStats(s => ({...s, ...r.data}))).catch(()=>{});
    api.get("/testimonials").then(r => setTestimonials(r.data)).catch(()=>{});
  }, []);

  return (
    <div className="min-h-screen hero-radial">
      <Navbar />

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 pt-16 lg:pt-24 pb-20">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 rise-in">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-brand-gold/40 bg-white text-xs uppercase tracking-[0.25em] gold-text mb-8" data-testid="hero-badge">
              <Sparkles size={12}/> Premium GCSE Maths · Est. Wokingham
            </div>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-semibold tracking-tighter-x leading-[0.95] brand-navy">
              Unlock the <em className="italic gold-text">genius</em><br/>
              within your child.
            </h1>
            <p className="mt-8 text-lg md:text-xl text-slate-600 max-w-2xl leading-relaxed">
              Year 7 through 11. Foundation & Higher. AQA · Edexcel · OCR.
              A personalised tuition programme built around a <strong className="brand-navy">{stats.questions.toLocaleString()}+ question bank</strong>, small-group live classes, and an AI maths companion available 24/7.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <Link to="/signup" className="btn-gold px-8 py-4 rounded-md text-base inline-flex items-center justify-center gap-2" data-testid="hero-cta-trial">
                Book a free trial lesson <ArrowRight size={18}/>
              </Link>
              <Link to="/gcse" className="btn-ghost-navy px-8 py-4 rounded-md text-base inline-flex items-center justify-center gap-2" data-testid="hero-cta-curriculum">
                Explore the curriculum
              </Link>
            </div>
            <p className="mt-4 text-xs text-slate-500">No card required · 45-minute diagnostic · Wokingham or online</p>
            <div className="mt-12 grid grid-cols-3 gap-6 max-w-xl">
              <Stat n={`${stats.questions.toLocaleString()}+`} l="Question bank"/>
              <Stat n="100s" l="Of students helped"/>
              <Stat n={`+${stats.avg_grade_improvement} grades`} l="Avg. improvement"/>
            </div>
          </div>

          <div className="lg:col-span-5 rise-in delay-2">
            <div className="relative">
              <div className="absolute -inset-3 bg-brand-gold/20 rounded-xl blur-2xl"></div>
              <div className="relative rounded-xl overflow-hidden border border-slate-200 shadow-2xl">
                <img src="https://images.pexels.com/photos/8423463/pexels-photo-8423463.jpeg" alt="Student studying maths" className="w-full h-[520px] object-cover" />
                <div className="absolute bottom-4 left-4 right-4 glass rounded-lg p-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-brand-navy flex items-center justify-center">
                    <Trophy size={18} className="brand-gold"/>
                  </div>
                  <div className="text-sm">
                    <div className="brand-navy font-semibold">Grade 9 achieved</div>
                    <div className="text-slate-500 text-xs">Sarah, Year 11 · 9 months of tuition</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Exam board strip */}
      <section className="border-y border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8 flex flex-wrap items-center justify-between gap-6">
          <span className="text-xs uppercase tracking-[0.3em] text-slate-500">Aligned with every major UK exam board</span>
          <div className="flex flex-wrap items-center gap-8 md:gap-14 font-serif text-xl brand-navy">
            <span data-testid="board-aqa">AQA</span>
            <span data-testid="board-edexcel">Edexcel</span>
            <span data-testid="board-ocr">OCR</span>
            <span className="text-slate-400 text-base">· Foundation & Higher Tier</span>
          </div>
        </div>
      </section>

      {/* Approach */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
        <div className="grid md:grid-cols-12 gap-10 mb-16">
          <div className="md:col-span-4">
            <div className="text-xs uppercase tracking-[0.3em] gold-text mb-4">Our approach</div>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tighter-x brand-navy">A system built for grade 9, not just grade 5.</h2>
          </div>
          <p className="md:col-span-8 md:col-start-6 text-lg text-slate-600 leading-relaxed">
            We don't believe in generic worksheets. Each student follows a clear weekly rhythm: a focused live class (90–120 min), carefully selected practice from our {stats.questions.toLocaleString()}-question vault, timed mock drills, and a private error book — the same method we've used to take borderline students to Grade 9.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Feature icon={<Target/>} title="Diagnostic First" desc="A 20-minute assessment identifies the exact topics holding your child back — no guesswork."/>
          <Feature icon={<BookOpen/>} title="Curated Material" desc="Worksheets, past-paper drills, and video walkthroughs — carefully curated from the best sources across the industry."/>
          <Feature icon={<Users/>} title="Small-Group Classes" desc="Maximum 8 students per batch. Small enough to know every learner; large enough for healthy discussion."/>
          <Feature icon={<Brain/>} title="AI Tutor, 24/7" desc="Stuck at 10pm? Our Claude-powered maths companion walks through solutions step-by-step."/>
        </div>
      </section>

      {/* Question Bank spotlight */}
      <section className="panel-navy">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-6">
            <div className="text-xs uppercase tracking-[0.3em] brand-gold mb-4">The Vault</div>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight">
              <span className="italic">{stats.questions.toLocaleString()}+</span> curated GCSE maths questions. <span className="brand-gold">Filtered in seconds.</span>
            </h2>
            <p className="mt-6 text-white/70 text-lg max-w-xl">
              Built and classified over 10 years by our senior tutors — every question tagged by board, tier, year, topic and difficulty. Practice only what matters.
            </p>
            <div className="mt-10 grid grid-cols-2 gap-4">
              <Pill>AQA · Foundation</Pill>
              <Pill>Edexcel · Higher</Pill>
              <Pill>OCR · Year 11</Pill>
              <Pill>Mock Papers 2019–2025</Pill>
            </div>
          </div>
          <div className="md:col-span-6">
            <div className="rounded-xl border border-white/10 bg-white/5 p-6 backdrop-blur-sm">
              <div className="flex gap-2 mb-4 text-[11px] uppercase tracking-widest">
                <span className="px-2 py-1 rounded bg-brand-gold text-brand-navy font-semibold">Edexcel</span>
                <span className="px-2 py-1 rounded border border-white/20 text-white/70">Higher</span>
                <span className="px-2 py-1 rounded border border-white/20 text-white/70">Year 10</span>
                <span className="px-2 py-1 rounded border border-white/20 text-white/70">Quadratics</span>
              </div>
              <div className="font-serif text-2xl leading-relaxed">Solve x² + 5x + 6 = 0</div>
              <div className="mt-6 space-y-2 text-sm">
                {["x = −2, −3", "x = 2, 3", "x = −1, −6", "x = 1, 6"].map((o,i)=>(
                  <div key={i} className={`px-4 py-3 rounded border ${i===0 ? 'border-brand-gold bg-brand-gold/10':'border-white/10'}`}>
                    <span className="text-white/50 mr-3">{String.fromCharCode(65+i)}.</span> {o}
                    {i===0 && <CheckCircle2 className="inline ml-2 brand-gold" size={16}/>}
                  </div>
                ))}
              </div>
              <div className="mt-6 pt-6 border-t border-white/10 text-sm text-white/70">
                <strong className="brand-gold">Worked solution: </strong>
                Factorise as (x+2)(x+3) = 0 → x = −2 or x = −3.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Dashboards */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
        <div className="text-center mb-16">
          <div className="text-xs uppercase tracking-[0.3em] gold-text mb-4">Two portals. One family.</div>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tighter-x brand-navy">Dashboards for students and parents.</h2>
          <p className="text-slate-500 mt-4 text-sm">Parent login activates after your first trial lesson.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-8">
          <DashCard
            title="Student Portal"
            badge="For Year 7–11"
            img="https://images.pexels.com/photos/4145190/pexels-photo-4145190.jpeg"
            points={["Progress charts by topic","Practice quizzes with instant marking","AI maths tutor chat","Upcoming lesson schedule"]}
          />
          <DashCard
            title="Parent Portal"
            badge="For guardians"
            img="https://images.pexels.com/photos/8422186/pexels-photo-8422186.jpeg"
            points={["Weekly progress reports","Child's quiz & attendance history","Unlock weekly mock tests by topic","Transparent lesson schedule & billing"]}
          />
        </div>
      </section>

      {/* How it works */}
      <section className="bg-white border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
          <div className="text-center mb-16">
            <div className="text-xs uppercase tracking-[0.3em] gold-text mb-4">How it works</div>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tighter-x brand-navy">Four steps to starting with us.</h2>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              {n:"01", icon:<Send size={20}/>, t:"Submit the form", d:"Fill in your details. Takes under a minute, no card needed."},
              {n:"02", icon:<Phone size={20}/>, t:"We call within 24 h", d:"A quick chat about your child's level, target grade and availability."},
              {n:"03", icon:<CalendarDays size={20}/>, t:"Free trial lesson", d:"A 45-minute diagnostic session at our Wokingham branch or online."},
              {n:"04", icon:<GraduationCap size={20}/>, t:"Personalised plan", d:"We share a written plan and confirm your place in a class."},
            ].map(s => (
              <div key={s.n} className="lift-card bg-slate-50 border border-slate-200 rounded-lg p-6 relative" data-testid={`hiw-step-${s.n}`}>
                <div className="font-serif text-5xl gold-text leading-none mb-4 opacity-30">{s.n}</div>
                <div className="w-10 h-10 rounded-md bg-brand-navy flex items-center justify-center mb-4" style={{color:"#D4AF37"}}>{s.icon}</div>
                <h4 className="font-serif text-xl brand-navy mb-2">{s.t}</h4>
                <p className="text-sm text-slate-600 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/signup" className="btn-gold px-8 py-3 rounded-md inline-flex items-center gap-2" data-testid="hiw-cta"><ArrowRight size={16}/> Start step 1</Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="bg-white border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
          <div className="flex items-end justify-between mb-12">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] gold-text mb-4">Families talking</div>
              <h2 className="font-serif text-4xl md:text-5xl tracking-tighter-x brand-navy">Results families write home about.</h2>
            </div>
            <div className="hidden md:flex items-center gap-1">
              {[1,2,3,4,5].map(i => <Star key={i} size={18} fill="#D4AF37" stroke="#D4AF37"/>)}
              <span className="ml-2 text-sm text-slate-600 font-medium">Trusted by hundreds of families</span>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.slice(0,6).map(t => (
              <div key={t.id} className="lift-card bg-slate-50 border border-slate-200 rounded-lg p-6" data-testid={`testimonial-${t.id}`}>
                <Quote className="gold-text mb-4" size={22}/>
                <p className="text-slate-700 leading-relaxed text-sm mb-6">"{t.quote}"</p>
                <div className="border-t border-slate-200 pt-4 flex justify-between items-center">
                  <div>
                    <div className="brand-navy font-semibold text-sm">{t.name}</div>
                    <div className="text-xs text-slate-500">{t.role} · {t.year}</div>
                  </div>
                  {t.grade_achieved && <span className="text-[10px] uppercase px-2 py-1 rounded bg-brand-navy text-white tracking-widest">{t.grade_achieved}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
        <div className="relative rounded-2xl overflow-hidden panel-navy p-12 md:p-16 grain">
          <div className="relative z-10 max-w-2xl">
            <div className="text-xs uppercase tracking-[0.3em] brand-gold mb-4">Free trial</div>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight mb-6">Try a session on us. No commitment.</h2>
            <p className="text-white/75 text-lg mb-10">Book a free diagnostic lesson. In 45 minutes we'll identify exactly what your child needs and recommend a plan.</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/signup" className="btn-gold px-8 py-4 rounded-md inline-flex items-center gap-2" data-testid="cta-signup">
                Book free trial <ArrowRight size={18}/>
              </Link>
              <Link to="/contact" className="px-8 py-4 rounded-md inline-flex items-center gap-2 border border-white/25 hover:bg-white/5 transition" data-testid="cta-contact">
                <Clock size={18}/> Speak to a tutor
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer/>
    </div>
  );
}

function Stat({n,l}){return(<div><div className="font-serif text-3xl brand-navy">{n}</div><div className="text-xs uppercase tracking-widest text-slate-500 mt-1">{l}</div></div>);}
function Feature({icon,title,desc}){return(<div className="lift-card p-6 rounded-lg bg-white border border-slate-200"><div className="w-11 h-11 rounded-md bg-brand-navy flex items-center justify-center brand-gold mb-4" style={{color:"#D4AF37"}}>{icon}</div><h3 className="font-serif text-xl brand-navy mb-2">{title}</h3><p className="text-sm text-slate-600 leading-relaxed">{desc}</p></div>);}
function Pill({children}){return <div className="px-4 py-3 rounded border border-white/20 text-sm flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-brand-gold"></span>{children}</div>;}
function DashCard({title,badge,img,points}){return(<div className="lift-card rounded-xl overflow-hidden border border-slate-200 bg-white"><div className="h-52 overflow-hidden"><img src={img} alt={title} className="w-full h-full object-cover"/></div><div className="p-8"><span className="text-[10px] uppercase tracking-[0.25em] gold-text">{badge}</span><h3 className="font-serif text-3xl brand-navy mt-2 mb-5">{title}</h3><ul className="space-y-3">{points.map((p,i)=>(<li key={i} className="flex gap-3 text-sm text-slate-700"><CheckCircle2 size={18} className="gold-text shrink-0"/>{p}</li>))}</ul></div></div>);}
